<template>
  <div class="app-layout" @mousedown="mousedownInWindow">
    <component
      :is="DevToolToggle"
      v-if="dev && DevToolToggle"
      @toggle="toggleDevTool"
    />
    <app-header v-if="isUserLoaded" />
    <router-view />
    <component :is="DevFetchTool" v-if="dev && showDevTool && DevFetchTool" />
  </div>
</template>

<script setup lang="ts">
import '../../assets/index.scss'
import AppHeader from './AppHeader.vue'
import { useRouter } from 'vue-router'
import { useUserProfileStore } from '../store/userProfile'
import { useAuthApiStore } from '@/src/shared/auth/authApiStore'
import { useAuthOrchestrator } from '@/src/shared/auth/useAuthOrchestrator'
import {
  ref,
  computed,
  onBeforeMount,
  onMounted,
  onBeforeUnmount,
  defineAsyncComponent,
} from 'vue'

// Conditionally import dev components only in DEV environment
const DevFetchTool = import.meta.env.DEV
  ? defineAsyncComponent(() => import('@/src/components/DevFetchTool.vue'))
  : null

const DevToolToggle = import.meta.env.DEV
  ? defineAsyncComponent(() => import('@/src/components/DevToolToggle.vue'))
  : null

const userProfileStore = useUserProfileStore()
const router = useRouter()
const authApiStore = useAuthApiStore()
const dev = import.meta.env.DEV === true
const showDevTool = ref(false)

// Set app type immediately (before orchestrator) for clarity
authApiStore.setAppType('buyer')

const _keyListener = ref<((e: KeyboardEvent) => void) | null>(null)

// Add a computed property to check if user data is actually loaded
const isUserLoaded = computed(() => {
  return (
    userProfileStore.isLoggedIn &&
    userProfileStore.currentUser &&
    Object.keys(userProfileStore.currentUser).length > 0 &&
    userProfileStore.currentUser.id
  )
})

// Set up authentication orchestration AFTER app type is set
const { stopAuthWatcher } = useAuthOrchestrator({
  verifyUser: () => userProfileStore.verify(),
})

onBeforeMount(() => {
  const storedUserId = localStorage.getItem('currentUserId')
  const storedToken = localStorage.getItem('currentUserToken')

  // If token exists but userId doesn't, clear authentication
  if (!storedUserId && storedToken) {
    console.log(
      'Invalid auth state: token exists but no user ID - clearing credentials'
    )
    authApiStore.clearUserCredentials()
  }

  // Check for automatic login - orchestrator will handle verification
  const userId = authApiStore.currentUserId
  const token = authApiStore.accessToken

  if (userId && token) {
    userProfileStore.setLoggedIn(true)
    authApiStore.handleLoginSuccess(userId, token)

    // Handle navigation after authentication
    if (router.currentRoute.value.path === '/login') {
      router.push({ name: 'Projects' })
    }
  }
})

onMounted(() => {
  _keyListener.value = function (e: KeyboardEvent) {
    if (e.key === 'p' && e.altKey) {
      e.preventDefault()
      router.push({ name: 'projects' })
    }
    if (e.key === '/' && e.altKey) {
      e.preventDefault()
      router.push({ name: 'globalSearch' })
    }
  }

  document.addEventListener('keydown', _keyListener.value)
})

onBeforeUnmount(() => {
  if (_keyListener.value) {
    document.removeEventListener('keydown', _keyListener.value)
  }
  stopAuthWatcher()
})

function toggleDevTool() {
  showDevTool.value = !showDevTool.value
}

function mousedownInWindow() {
  // Add implementation if needed
}
</script>

<style scoped>
/* App-specific styles can go here */
</style>
